import { PageHeader } from '@ant-design/pro-layout'
import type { ViewFieldsFragment } from '@salescore/client-api'
import { useMe } from '@salescore/client-common'
import {
  type AdditionalFilterLeaf,
  CORE_CONSTANT,
  generateFieldName,
  type ViewConfigDimensionFilter,
  type ViewConfigFilter,
  type ViewConfigKpi,
  type ViewConfigKpiParameter,
  type ViewConfigSheet,
} from '@salescore/core'
import { Divider, Empty, Space, Tag } from 'antd'
import { Array, Match, pipe, Record } from 'effect'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { type ReactElement, useMemo } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'

import { useSetChildViewId } from '../hooks/useSetChildViewId'
import { type DrillDownParameterForKpiPivot, kpiParameterAtom } from '../recoil/navigation/atoms'
import { useNavigationModal } from '../recoil/navigation/hooks'
import { additionalConfigAtom } from '../recoil/records/atoms'
import { viewsContextAtom } from '../recoil/view/atoms'
import { getMetricFromViewConfigKpi } from '../recoil/view/attachMetricToKpiConfig'
import { useGetModel, useGetModelProperty } from '../recoil/view/selectors/connectionsSelector'
import { DurationTimerStart } from './misc/DurationTimer'
import { ViewComponent, type ViewsContext } from './ViewComponent'

interface AdditionalFilterLeafWithOverride extends AdditionalFilterLeaf {
  override?: { label?: string }
}

export function getDimensionsWithValue(
  parameter: DrillDownParameterForKpiPivot,
  pivot: ViewConfigKpiParameter['pivot'],
) {
  const rowDimensionsWithValue = pivot.rows.map((x, index) => ({
    key: x.key,
    value: parameter.dimensionValues.rows[index],
    dimension: x,
  }))
  const columnDimensionsWithValue = pivot.columns.map((x, index) => ({
    key: x.key,
    value: parameter.dimensionValues.columns[index],
    dimension: x,
  }))
  return [...rowDimensionsWithValue, ...columnDimensionsWithValue]
}

// eslint-disable-next-line sonarjs/todo-tag
// TODO: 煩雑になってきたので、ユースケース毎にコンポーネントを分離させてもいいかも

export const ViewDrillDownModalContentForKpi = ({
  kpiView,
  parameter,
  onFinish,
  viewsContextOverride,
  view,
}: {
  kpiView: ViewFieldsFragment & { config: ViewConfigKpi }
  parameter: DrillDownParameterForKpiPivot
  onFinish: () => Promise<void>
  viewsContextOverride?: Partial<ViewsContext>
  view: ViewFieldsFragment
}): ReactElement => {
  const { organization } = useMe()
  const { drillDownModal } = useNavigationModal()
  const additionalConfig = useRecoilValue(additionalConfigAtom)
  const { goalConfig } = useRecoilValue(kpiParameterAtom)
  const viewsContext = useRecoilValue(viewsContextAtom)
  const [kpiPivotParameter] = useRecoilState(kpiParameterAtom)
  const viewConfigPivot = kpiPivotParameter.pivot
  const dimensionsWithValue = getDimensionsWithValue(parameter, viewConfigPivot)

  const originalViewId = kpiView.id.replace(CORE_CONSTANT.KPI_PREVIEW_VIEW_ID_PREFIX, '')
  const drillDownViewId = kpiView.id.startsWith(CORE_CONSTANT.KPI_PREVIEW_VIEW_ID_PREFIX)
    ? `${CORE_CONSTANT.KPI_PREVIEW_DRILL_DOWN_VIEW_ID_PREFIX}${originalViewId}`
    : kpiView.id
  useSetChildViewId(drillDownViewId, view)

  // KPIのモーダルなので、ここでsheetの情報を取得することは本来は不要だが、
  //
  const metric = getMetricFromViewConfigKpi(kpiView.config)
  const sheetViewConfig = metric?.sheet
  if (sheetViewConfig === undefined) {
    return <Empty description={<div>{t(`シートが見つかりません。`)}</div>} />
  }

  // additionalFilterTreesをここで求めようと思っていたが、compile側に移行した。表示で必要になったら要検討
  // const actualMeasure = kpiView.config.measures.find((x) => x.measureRole === 'actual')
  // const measure = (sheetViewConfig.fields ?? [])
  //   .flatMap((x) => x.measures ?? [])
  //   .find((x) => x.name === actualMeasure?.measureWithDimension.measureName)
  // const additionalFilterTrees: ViewConfigAdditionalConfig['filterTrees'] =
  //   measure?.type === 'preset' && measure.filterTree !== undefined
  //     ? [
  //         {
  //           type: 'measure',
  //           tree: measure.filterTree,
  //         },
  //       ]
  //     : undefined
  const additionalFilterLeafs = dimensionsWithValue
    .map((x): ViewConfigDimensionFilter | undefined => {
      if (x.value?.value === CORE_CONSTANT.KPI_PIVOT_TOTAL_STRING) {
        return undefined
      }
      if (x.key === CORE_CONSTANT.KPI_PIVOT_KPI_DIMENSION.key) {
        return undefined
      }
      if (x.key === CORE_CONSTANT.KPI_PIVOT_KPI_GROUP_DIMENSION().key) {
        return undefined
      }

      return {
        type: `dimension`,
        dimension: x.dimension,
        filterValue: x.value?.value === CORE_CONSTANT.KPI_PIVOT_NULL_STRING ? undefined : x.value?.value,
        filterType: x.value?.value === CORE_CONSTANT.KPI_PIVOT_NULL_STRING ? 'null' : 'equal',
        filterValueLabel: x.value?.value === CORE_CONSTANT.KPI_PIVOT_NULL_STRING ? `なし` : x.value?.label,
        filterDateTimeType: x.dimension.dateSpan,
        option: {
          isDrillDownFilter: true,
        },
      }
    })
    .filter(isNotNullish)
  const additionalFilterLeafsWithPrevious: AdditionalFilterLeafWithOverride[] =
    parameter.additionalFilterLeafsWithPrev ?? [
      ...(additionalConfig.filterLeafs ?? []),
      ...additionalFilterLeafs.map((leaf) => ({ type: 'drillDown' as const, leaf })),
      // 軸フィルタも明示した方がいい気がするが、ダッシュボードによっては煩雑になりそう。一旦非表示
      // ...(kpiPivotParameter.dimensionFilterLeafs?.map(leaf => ({ leaf, type: 'dimension' as const })) ?? []),
    ]

  const kpiOrKpiFragment = kpiView.config

  return (
    <PageHeader
      title={
        <Space className="px-4 py-2">
          {kpiView.name}
          {/* eslint-disable-next-line sonarjs/todo-tag */}
          {/* TODO: treeをいい感じに表示する？そもそも表示する必要はあるだろうか？ */}
          {additionalFilterLeafsWithPrevious.map((leaf, index) => (
            <AdditionalFilterLeafTag
              key={`${leaf.leaf.type}_${index}`}
              leaf={leaf.leaf}
              config={sheetViewConfig}
              override={leaf.override}
            />
          ))}
          {/* periodNameの表示 */}
          {pipe(
            parameter.queryParameter ?? kpiPivotParameter.queryParameter ?? {},
            Record.values,
            Array.filter(isNotNullish),
            Array.map((x) => {
              // カスタム(Period)では startAt/endAt が時間込みで表示されるため除外する
              const deleteRegexp = /\d{2}:\d{2}:\d{2}$/v
              return x.replace(deleteRegexp, '')
            }),
            Array.map((x, index) => (
              <Tag key={`${x}_${index}`}>
                {
                  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                  t(x) ?? x
                }
              </Tag>
            )),
          )}
        </Space>
      }
      style={{
        padding: 0,
      }}
      className="w-full overflow-hidden"
    >
      <Divider style={{ margin: 0 }} />
      <div
        style={{
          height: 'calc(100vh - 150px)', // 画面一杯に表示するために、100vhからモーダル・タイトルをざっくり引いた値を指定
        }}
      >
        <ViewComponent
          view={{
            ...kpiView,
            config: kpiOrKpiFragment,
            id: drillDownViewId,
          }}
          parentAbility={undefined}
          parameter={parameter.queryParameter ?? kpiPivotParameter.queryParameter ?? {}}
          additionalConfig={{
            filterLeafs: additionalFilterLeafsWithPrevious.map((x) => ({ type: x.type, leaf: x.leaf })),
            previousDimmensions: kpiPivotParameter.pivot.rows,
            kpiParameter: {
              pivot: {
                rows: kpiPivotParameter.pivot.rows,
                // eslint-disable-next-line sonarjs/todo-tag
                columns: [], // TODO: 元々 rows も空にしていた。 columns も非空にできたらしたら良いのかもしれない。
                columnsForDrilldownComments: kpiPivotParameter.pivot.columns,
              },
              invisibleKpiIds: kpiPivotParameter.invisibleKpiIds,
              sorterForDrilldownComments: kpiPivotParameter.sorter,
              dimensionFilterLeafs: kpiPivotParameter.dimensionFilterLeafs,
              queryParameter: parameter.queryParameter ?? kpiPivotParameter.queryParameter ?? {},
              goalConfig,
              enableHistoricalGroupResolution: kpiPivotParameter.enableHistoricalGroupResolution,
            },
          }}
          onFinish={() => {
            void onFinish()
          }}
          viewsContext={{
            ...viewsContext,
            ...viewsContextOverride,
            isDrillDown: true,
            prevView: view,
            kpiPivotPresetName: parameter.kpiPivotPresetName,
            onChangesChange: (hasChanges) => {
              viewsContext.onChangesChange?.(hasChanges)
              drillDownModal.setInnerState({ hasChanges })

              // 一度でも変更があればtrueにする
              if (hasChanges && viewsContext.setIsChangedDrillDownModal !== undefined) {
                viewsContext.setIsChangedDrillDownModal(true)
              }
            },
            isChangedDrillDownModal: viewsContext.isChangedDrillDownModal,
            setIsChangedDrillDownModal: viewsContext.setIsChangedDrillDownModal,
          }}
        />
      </div>
      <DurationTimerStart viewId={drillDownViewId} organizationId={organization.id} trigger="drillDownModalOpened" />
    </PageHeader>
  )
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
function AdditionalFilterLeafTag({
  leaf,
  config,
  override,
}: {
  leaf: ViewConfigFilter
  config: ViewConfigSheet
  override?: { label?: string }
}): ReactElement {
  const getModelProperty = useGetModelProperty()
  const getModel = useGetModel()

  const value = leaf.filterValueLabel ?? leaf.filterValue
  const defaultLabel = useMemo(
    () =>
      Match.value(leaf).pipe(
        Match.when({ type: 'property' }, (leaf) => {
          if (leaf.property.propertyName === 'id') {
            const model = getModel(leaf.property.modelName)
            return model?.label ?? 'id'
          }

          const property = getModelProperty(leaf.property.modelName, leaf.property.propertyName)
          return property?.label
        }),
        Match.when({ type: 'field' }, (leaf) => {
          const field = (config.fields ?? []).find((x) => generateFieldName(x.property) === leaf.fieldName)
          const property = getModelProperty(field?.property.modelName, field?.property.propertyName)
          return property?.label
        }),
        Match.when({ type: 'dimension' }, (leaf) => leaf.dimension.label),
        Match.when({ type: 'custom' }, (leaf) => leaf.label ?? t('カスタムフィルタ')),
        Match.orElseAbsurd,
      ),
    [config.fields, getModel, getModelProperty, leaf],
  )

  return (
    <Tag>
      <>
        {override?.label ?? defaultLabel ?? value}: {value}
      </>
    </Tag>
  )
}
